import React, { useState } from "react";
import { Alert, Button, Table } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";

import { Customer } from "../../../../types/inventory/CustomerTypes";
import { Invoice } from "../../../../types/finance/FinanceTypes";
import { fetchInvoices, fetchClinicSettings } from "../../../../api/finance/FinanceApi";
import { financeKeys } from "../../../../api/finance/financeQueryKeys";
import { canRaiseInvoices } from "../../../../utils/userPrefs/validatePermissions";
import { formatCents } from "../../../../utils/finance/money";
import { invoiceDisplayNumber, invoiceStatusPresentation } from "../../../../utils/finance/invoiceDisplay";
import { CreateInvoiceModal } from "../../../../components/finance/invoicing";
import { SectionCard, StatusTag, EmptyState, InlineLoading } from "../../../../components/shared";
import { customerDisplayName } from "../../../../types/inventory/customerRecord";

/**
 * CU-86d45b54n — customer-scoped invoicing on the generic Finance contract: invoices are keyed by the
 * customer's numeric auth user id, and the shared CreateInvoiceModal is fixed to that id with
 * Customer wording. Gated on canRaiseInvoices() exactly as the patient record's section is.
 */
const CustomerInvoicesSection: React.FC<{ customer: Customer }> = ({ customer }) => {
	const permitted = canRaiseInvoices();
	const userId = String(customer.id);
	const listParams = { userId, per_page: 10, sort: "createdAt" as const, order: "desc" as const };

	const [modalOpen, setModalOpen] = useState(false);

	const { data: settings } = useQuery({
		queryKey: financeKeys.clinicSettings(),
		queryFn: fetchClinicSettings,
		enabled: permitted,
		staleTime: 5 * 60 * 1000,
	});

	const { data, isLoading, isError, isFetching, refetch } = useQuery({
		queryKey: financeKeys.invoiceList(listParams),
		queryFn: () => fetchInvoices(listParams),
		enabled: permitted,
	});

	if (!permitted) {
		return null;
	}

	const invoices: Invoice[] = data?.data ?? [];

	const columns = [
		{
			title: "Number",
			key: "number",
			render: (_: unknown, inv: Invoice) => invoiceDisplayNumber(inv),
		},
		{ title: "Issued", dataIndex: "issueDate", key: "issueDate", width: 120 },
		{
			title: "Status",
			key: "status",
			width: 120,
			render: (_: unknown, inv: Invoice) => {
				const status = inv.effectiveStatus ?? inv.status;
				const presentation = invoiceStatusPresentation(status);
				return <StatusTag status={status} color={presentation.color} label={presentation.label} />;
			},
		},
		{
			title: "Total",
			key: "total",
			width: 110,
			align: "right" as const,
			render: (_: unknown, inv: Invoice) => formatCents(inv.totalIncCents),
		},
		{
			title: "Balance",
			key: "balance",
			width: 110,
			align: "right" as const,
			render: (_: unknown, inv: Invoice) => formatCents(inv.balanceDueCents),
		},
	];

	return (
		<SectionCard
			title="Invoices"
			extra={
				<Button
					type="primary"
					icon={<PlusOutlined />}
					onClick={() => setModalOpen(true)}
					data-testid="customer-raise-invoice"
				>
					Raise invoice
				</Button>
			}
		>
			{/* CU-86d45b54n — isLoading is false while an errored query refetches, so gate on isFetching too. */}
			{isLoading || (isError && isFetching) ? (
				<InlineLoading message="Loading invoices…" minHeight={120} />
			) : isError ? (
				<Alert
					type="error"
					showIcon
					message="Unable to load invoices"
					description="The customer's invoice history could not be retrieved."
					action={<Button onClick={() => void refetch()}>Retry</Button>}
					data-testid="customer-invoices-error"
				/>
			) : invoices.length === 0 ? (
				<EmptyState title="No invoices for this customer yet" />
			) : (
				<Table
					size="small"
					rowKey={(inv: Invoice) => inv.id}
					columns={columns}
					dataSource={invoices}
					pagination={false}
					data-testid="customer-invoices-table"
				/>
			)}

			<CreateInvoiceModal
				open={modalOpen}
				onClose={() => setModalOpen(false)}
				fixedUserId={userId}
				fixedRecipient={{ label: "Customer", name: customerDisplayName(customer) }}
				settings={settings ?? null}
			/>
		</SectionCard>
	);
};

export default CustomerInvoicesSection;
